import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, axios } from "@/api";
import { ArrowLeft, Bug, Sparkles, MessageSquare, CheckCircle, Loader2, Camera, X } from "lucide-react";
import { trackPageView, trackEvent } from "@/lib/track";
import { trackPostBirthdaySurveyCompleted } from "@/lib/events";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPostBdaySurvey = searchParams.get("from") === "post_birthday_survey";
  const [type, setType] = useState("feedback");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  // iter262 — optional photo attachment
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);       // File object
  const [imagePreview, setImagePreview] = useState(null); // data URL for thumbnail
  const [imageError, setImageError] = useState(null);

  useEffect(() => { trackPageView("feedback"); }, []);

  const handlePickImage = (e) => {
    setImageError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    // Content-type guard
    const ct = (f.type || "").toLowerCase();
    if (!ALLOWED_TYPES.includes(ct)) {
      setImageError("Unsupported format — please pick a JPG, PNG, or WEBP image.");
      e.target.value = "";
      return;
    }
    // Size guard
    if (f.size > MAX_BYTES) {
      setImageError("Image is too large — please keep attachments under 5 MB.");
      e.target.value = "";
      return;
    }
    setImage(f);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(f);
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setImageError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/feedback`, { feedback_type: type, message });
      const feedbackId = r?.data?.id;
      // Upload image as step 2 if present
      if (feedbackId && image) {
        const fd = new FormData();
        fd.append("file", image);
        try {
          await axios.post(`${API}/feedback/${feedbackId}/image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (imgErr) {
          // Non-blocking — surface but still count the text feedback as submitted.
          const detail = imgErr?.response?.data?.detail;
          setImageError(typeof detail === "string" ? detail : "Image upload failed — try a different photo.");
        }
      }
      trackEvent("feedback_submit", { type, has_image: !!image });
      if (isPostBdaySurvey) {
        trackPostBirthdaySurveyCompleted(null, 1);
      }
      setSubmitted(true);
    }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="feedback-page">
      <div className="bg-white border-b border-slate-200 p-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-slate-100" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-pink-500" />Send Feedback</h1>
      </div>
      <div className="px-4 mt-4 max-w-lg mx-auto">
        {submitted ? (
          <div className="text-center py-16"><div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4"><CheckCircle className="w-8 h-8 text-green-500" /></div><p className="text-lg font-bold text-slate-800">Thanks for your feedback!</p><p className="text-slate-500 text-sm mt-2">We'll review it and make improvements.</p><button onClick={() => navigate(-1)} className="mt-6 px-8 py-3 rounded-xl bg-sky-500 text-white font-semibold shadow-lg" data-testid="feedback-done">Done</button></div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button onClick={() => setType("feedback")} className={`flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${type === "feedback" ? "bg-sky-500 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="type-feedback"><MessageSquare className="w-4 h-4" />Feedback</button>
              <button onClick={() => setType("feature")} className={`flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${type === "feature" ? "bg-purple-500 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="type-feature"><Sparkles className="w-4 h-4" />Feature</button>
              <button onClick={() => setType("bug")} className={`flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${type === "bug" ? "bg-red-500 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="type-bug"><Bug className="w-4 h-4" />Bug</button>
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm bg-white min-h-[150px]" placeholder="Tell us what you think..." data-testid="feedback-message" />

            {/* iter262 — Optional photo attachment row */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition"
                data-testid="feedback-attach-image-btn"
              >
                <Camera className="w-4 h-4 text-sky-500" />
                {image ? "Change photo" : "Attach photo"}
                <span className="text-[10px] text-slate-400 font-normal ml-1">(JPG/PNG/WEBP · 5MB)</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                capture="environment"
                onChange={handlePickImage}
                className="hidden"
                data-testid="feedback-image-input"
              />
            </div>

            {imagePreview && (
              <div
                className="relative inline-block rounded-xl overflow-hidden border border-slate-200 shadow-sm"
                data-testid="feedback-image-preview"
              >
                <img src={imagePreview} alt="Attachment preview" className="max-h-40 max-w-full object-cover" />
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-1.5 right-1.5 bg-black/70 text-white rounded-full p-1 hover:bg-black/90"
                  data-testid="feedback-image-clear-btn"
                  aria-label="Remove photo"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {imageError && (
              <div
                className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700"
                data-testid="feedback-image-error"
              >
                {imageError}
              </div>
            )}

            <button onClick={handleSubmit} disabled={!message.trim() || loading} className="w-full py-4 rounded-xl bg-sky-500 text-white font-semibold shadow-lg disabled:opacity-50" data-testid="feedback-submit">{loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Submit Feedback"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
